import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import CustomText from "../common/general/CustomText/CustomText";
import Loader from "../common/status/Loader/Loader";
import LTIErrorState from "../../views/LTIErrorState";
import useDeepLinkAutoSubmit from "../../hooks/useDeepLinkAutoSubmit";

type DeepLinkAutoSubmitProps = {
  /** Signed LTI Deep Linking Response JWT from the backend. */
  jwt: string;
  /** The LMS endpoint that receives the deep linking response form POST. */
  deepLinkReturnUrl: string;
};

/**
 * Presentational component for the final step of the LTI deep linking flow.
 *
 * Delegates the form POST side-effect to `useDeepLinkAutoSubmit` and renders
 * the appropriate loading or error UI based on the hook's status.
 */
function DeepLinkAutoSubmit({ jwt, deepLinkReturnUrl }: DeepLinkAutoSubmitProps) {
  const { t } = useTranslation();
  const status = useDeepLinkAutoSubmit(jwt, deepLinkReturnUrl);

  if (status === "error_not_web") {
    return (
      <LTIErrorState
        title={t("Unable to complete deep link")}
        message={t("This action is only supported in a web browser.")}
        recoveryMessage={t("Try launching this activity again from your LMS.")}
      />
    );
  }

  if (status === "error_missing_data") {
    return (
      <LTIErrorState
        title={t("Unable to complete deep link")}
        message={t("The deep link response could not be sent back to your LMS. Required data is missing.")}
        recoveryMessage={t("Try launching this activity again from your LMS.")}
      />
    );
  }

  return (
    <View style={styles.container} accessibilityRole="none" accessibilityLabel={t("Returning to your LMS")}>
      <Loader />
      <CustomText text={t("Returning to your LMS...")} size="m" useTranslationText={false} style={styles.message} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: 24,
  },
  message: {
    color: "#475467",
    textAlign: "center",
  },
});

export default DeepLinkAutoSubmit;
